import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBug, faLightbulb, faChartLine, faQuestionCircle, faFrown, faCommentDots, faExclamationTriangle, faFire } from '@fortawesome/free-solid-svg-icons';
import { api } from '../services/api';
import FeedbackModal from '../components/FeedbackModal';
import '../styles/FeedbackPage.css';

interface Feedback {
  id: string;
  type: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  category: string | null;
  upvotes: number;
  views: number;
  adminResponse: string | null;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    profilePicture: string | null;
  };
  _count: {
    comments: number;
    votes: number;
  };
  votes?: Array<{ userId: string }>;
}

const FeedbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [myFeedback, setMyFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'my'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const currentUserId = localStorage.getItem('userId');

  useEffect(() => {
    loadFeedback();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterType, filterStatus, sortBy]);

  const loadFeedback = async () => {
    try {
      setLoading(true);
      if (activeTab === 'my') {
        const response = await api.get('/feedback/my-feedback');
        setMyFeedback(response.data);
      } else {
        const params = new URLSearchParams();
        if (filterType) params.append('type', filterType);
        if (filterStatus) params.append('status', filterStatus);
        params.append('sortBy', sortBy);
        params.append('sortOrder', 'desc');

        const response = await api.get(`/feedback?${params.toString()}`);
        setFeedbackList(response.data.feedback || response.data);
      }
    } catch (error) {
      console.error('Error loading feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (feedbackId: string) => {
    try {
      await api.post(`/feedback/${feedbackId}/vote`);
      loadFeedback();
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, any> = {
      BUG_REPORT: faBug,
      FEATURE_REQUEST: faLightbulb,
      IMPROVEMENT: faChartLine,
      QUESTION: faQuestionCircle,
      COMPLAINT: faFrown,
      OTHER: faCommentDots
    };
    return <FontAwesomeIcon icon={icons[type] || faCommentDots} />;
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; label: string }> = {
      NEW: { color: '#667eea', label: 'New' },
      IN_REVIEW: { color: '#f6ad55', label: 'In Review' },
      PLANNED: { color: '#4299e1', label: 'Planned' },
      IN_PROGRESS: { color: '#9f7aea', label: 'In Progress' },
      COMPLETED: { color: '#48bb78', label: 'Completed' },
      REJECTED: { color: '#f56565', label: 'Rejected' },
      CLOSED: { color: '#718096', label: 'Closed' }
    };
    const badge = badges[status] || { color: '#718096', label: status };
    return (
      <span className="status-badge" style={{ backgroundColor: badge.color }}>
        {badge.label}
      </span>
    );
  };

  const getPriorityBadge = (priority: string) => {
    if (!priority || priority === 'LOW') return null;
    
    const badges: Record<string, { color: string; icon: any }> = {
      MEDIUM: { color: '#f6ad55', icon: faExclamationTriangle },
      HIGH: { color: '#f56565', icon: faFire },
      CRITICAL: { color: '#9f1239', icon: faFire }
    };
    const badge = badges[priority];
    if (!badge) return null;

    return (
      <span className="priority-badge" style={{ backgroundColor: badge.color }}>
        <FontAwesomeIcon icon={badge.icon} /> {priority}
      </span>
    );
  };

  const hasVoted = (feedback: Feedback) => {
    return feedback.votes?.some(v => v.userId === currentUserId) || false;
  };

  const displayFeedback = activeTab === 'my' ? myFeedback : feedbackList;

  return (
    <div className="feedback-page">
      <div className="feedback-header">
        <div className="header-content">
          <h1>📝 Feedback Center</h1>
          <p>Share your ideas, report bugs, and help us improve</p>
        </div>
        <button className="btn-new-feedback" onClick={() => setIsModalOpen(true)}>
          + New Feedback
        </button>
      </div>

      <div className="feedback-tabs">
        <button
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Feedback
        </button>
        <button
          className={`tab ${activeTab === 'my' ? 'active' : ''}`}
          onClick={() => setActiveTab('my')}
        >
          My Feedback
        </button>
      </div>

      {activeTab === 'all' && (
        <div className="feedback-filters">
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            <option value="BUG_REPORT">🐛 Bug Report</option>
            <option value="FEATURE_REQUEST">✨ Feature Request</option>
            <option value="IMPROVEMENT">📈 Improvement</option>
            <option value="QUESTION">❓ Question</option>
            <option value="COMPLAINT">😞 Complaint</option>
            <option value="OTHER">💬 Other</option>
          </select>

          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="NEW">New</option>
            <option value="IN_REVIEW">In Review</option>
            <option value="PLANNED">Planned</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="REJECTED">Rejected</option>
            <option value="CLOSED">Closed</option>
          </select>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="createdAt">Latest</option>
            <option value="upvotes">Most Voted</option>
            <option value="views">Most Viewed</option>
          </select>
        </div>
      )}

      {loading ? (
        <div className="loading-state">Loading feedback...</div>
      ) : displayFeedback.length === 0 ? (
        <div className="empty-state">
          <p>No feedback found</p>
          <button onClick={() => setIsModalOpen(true)}>Submit First Feedback</button>
        </div>
      ) : (
        <div className="feedback-list">
          {displayFeedback.map((feedback) => (
            <div
              key={feedback.id}
              className="feedback-item"
              onClick={() => navigate(`/feedback/${feedback.id}`)}
            >
              <div className="feedback-vote" onClick={(e) => {
                e.stopPropagation();
                handleVote(feedback.id);
              }}>
                <button className={`vote-btn ${hasVoted(feedback) ? 'voted' : ''}`}>
                  ▲
                </button>
                <span className="vote-count">{feedback.upvotes}</span>
              </div>

              <div className="feedback-content">
                <div className="feedback-meta">
                  <span className="feedback-type">{getTypeIcon(feedback.type)}</span>
                  {getStatusBadge(feedback.status)}
                  {getPriorityBadge(feedback.priority)}
                </div>

                <h3 className="feedback-title">{feedback.title}</h3>
                <p className="feedback-description">{feedback.description.substring(0, 200)}...</p>

                {feedback.adminResponse && (
                  <div className="admin-response-preview">
                    <strong>Admin Response:</strong> {feedback.adminResponse.substring(0, 100)}...
                  </div>
                )}

                <div className="feedback-footer">
                  <div className="feedback-author">
                    {feedback.user.profilePicture ? (
                      <img src={feedback.user.profilePicture} alt="" />
                    ) : (
                      <div className="avatar-placeholder">
                        {feedback.user.firstName[0]}{feedback.user.lastName[0]}
                      </div>
                    )}
                    <span>{feedback.user.firstName} {feedback.user.lastName}</span>
                  </div>

                  <div className="feedback-stats">
                    <span>💬 {feedback._count.comments}</span>
                    <span>👁️ {feedback.views}</span>
                    <span>{new Date(feedback.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <FeedbackModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={loadFeedback}
      />
    </div>
  );
};

export default FeedbackPage;
